import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'src/utils/i18n'
import { setCurrentTab, changeExtremeService } from 'src/store/apps/Common'
import { getAllDashboard, getLocalAndroidAll, getGcloudAll } from 'src/@api/dashboard'
import ChatHistory, { mapAuthContentsToSessions } from 'src/@core/components/chat-history'
import { EMPTY_TYPES } from 'src/components/EmptyState'
import { useDeviceScopedInit } from 'src/hooks/useDeviceScopedInit'
import { readLocalDeviceUdid, readUserEmail } from 'src/utils/readAndroidLocalContext'
import { buildTimeParams } from 'src/utils/dateRangeParams'

const API_BY_MODE = {
  ios: getAllDashboard,
  'android-local': getLocalAndroidAll,
  'android-cloud': getGcloudAll
}

/**
 * 按平台解析请求所需的 username / deviceUdid。
 * @param {'ios'|'android-local'|'android-cloud'} mode
 * @param {Object} context - deviceContext
 * @returns {{ username: string, deviceUdid: string }}
 */
const resolveRequestContext = (mode, context) => {
  if (mode === 'android-local') {
    return {
      username: readUserEmail(),
      deviceUdid: readLocalDeviceUdid(context)
    }
  }

  return {
    username: context?.username || '',
    deviceUdid: context?.physicalDeviceKey || ''
  }
}

/**
 * Shared page for AI chat history (ChatGPT / Gemini / …)
 * Category comes from sidebar route; mode picks the dashboard API.
 */
const AiChatHistoryPage = ({ category, title, mode = 'ios' }) => {
  const { t } = useTranslation('common')
  const dispatch = useDispatch()
  const isDisconnect = useSelector(state => state.Common.isDisconnect)
  const [loading, setLoading] = useState(true)
  const [sessions, setSessions] = useState([])
  const [loadError, setLoadError] = useState(false)
  const [hasDevice, setHasDevice] = useState(true)
  const scopeRef = useRef(null) // 当前设备作用域，用于日期变化时重拉
  const dateRangeRef = useRef({}) // { startTime, endTime }，空对象 = 不筛选

  useEffect(() => {
    const tabTitle = t(title) || title
    dispatch(setCurrentTab(tabTitle))

    return () => dispatch(setCurrentTab(''))
  }, [dispatch, t, title])

  // 拉取会话列表（设备切换、日期筛选共用）
  const load = useCallback(
    async ({ context, signal, isCurrent }) => {
      setLoading(true)
      setLoadError(false)

      const fetchApi = API_BY_MODE[mode] || getAllDashboard
      const { username, deviceUdid } = resolveRequestContext(mode, context)

      if (!isCurrent()) return
      setHasDevice(Boolean(deviceUdid))

      try {
        const response = await fetchApi(
          {
            username,
            params: {
              category,
              offset: 0,
              limit: 100,
              deviceUdid,
              ...dateRangeRef.current
            }
          },
          { signal }
        )

        if (mode === 'ios' && username != null && 'advanced' in (response || {})) {
          dispatch(changeExtremeService(['true', true].includes(response?.advanced)))
        }

        if (!isCurrent()) return

        const contents = response?.data?.contents
        setSessions(
          contents && Array.isArray(contents) && contents.length > 0
            ? mapAuthContentsToSessions(contents, category)
            : []
        )
      } catch (e) {
        if (!isCurrent() || e?.code === 'ERR_CANCELED') return
        setSessions([])
        setLoadError(true)
      } finally {
        if (isCurrent()) setLoading(false)
      }
    },
    [category, dispatch, mode]
  )

  // 设备切换时重置会话列表、清空日期筛选并重新拉取
  useDeviceScopedInit(
    useCallback(
      (_, scope) => {
        scopeRef.current = scope
        dateRangeRef.current = {}
        load(scope)
      },
      [load]
    )
  )

  // 日期筛选：带 startTime/endTime 重新请求接口（不是只筛已加载的数据）
  const handleDateChange = value => {
    dateRangeRef.current = value ? buildTimeParams([value, value]) : {}
    const scope = scopeRef.current
    if (scope) load(scope)
  }

  const emptyType = useMemo(() => {
    if (isDisconnect || !hasDevice) return EMPTY_TYPES.DEVICE_NOT_CONNECTED
    if (loadError) return EMPTY_TYPES.SYNC_FAILED

    return EMPTY_TYPES.NO_DATA
  }, [hasDevice, isDisconnect, loadError])

  return (
    <ChatHistory
      sessions={sessions}
      loading={loading}
      emptyType={emptyType}
      onDateChange={handleDateChange}
    />
  )
}

export default AiChatHistoryPage
